import React, { ReactNode } from 'react';
import { LayoutChangeEvent, Pressable, Text, View } from 'react-native';
import { Colors } from '../../../themes/colors';
import { Icon } from '../icons';
import { tokens } from '../tokens';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

export interface PanelHeaderProps {
  title: string;
  count?: number;
  onBackPress?: () => void;
  showBackButton?: boolean;
  /**
   * Inline content rendered immediately to the RIGHT of the title (e.g. a
   * list/calendar view selector). Sits in a `flexShrink: 0` wrapper, so the
   * title (in the `flexShrink: 1` / overflow-hidden left group) truncates first
   * and the accessory never clips. Distinct from `rightActions`, which stays at
   * the far right.
   */
  titleAccessory?: ReactNode;
  rightActions?: ReactNode;
  rightLinkText?: string;
  onRightLinkPress?: () => void;
  showRightLink?: boolean;
  onTitleLayout?: (event: LayoutChangeEvent) => void;
  backButtonTestID?: string;
  rightLinkTestID?: string;
}

export const PanelHeader: React.FC<PanelHeaderProps> = ({
  title,
  count,
  onBackPress,
  showBackButton = false,
  titleAccessory,
  rightActions,
  rightLinkText,
  onRightLinkPress,
  showRightLink = false,
  onTitleLayout,
  backButtonTestID,
  rightLinkTestID,
}) => {
  const titleContent = (
    <Text style={styles.titleText} numberOfLines={1}>
      {title}
      {count !== undefined && <Text style={styles.countText}> ({count})</Text>}
    </Text>
  );

  return (
    <View style={styles.header}>
      <View style={styles.headerLeft} onLayout={onTitleLayout}>
        {showBackButton ? (
          <Pressable
            style={styles.backButton}
            onPress={onBackPress}
            pointerEvents="box-only"
            testID={backButtonTestID}
          >
            <Icon name="Arrow Left" size={20} color={Colors.role.textStrong} />
            {titleContent}
          </Pressable>
        ) : (
          titleContent
        )}
      </View>
      {titleAccessory && (
        <View style={styles.titleAccessory}>{titleAccessory}</View>
      )}
      {showRightLink && rightLinkText && onRightLinkPress && (
        <Pressable
          style={styles.rightLink}
          onPress={onRightLinkPress}
          pointerEvents="box-only"
          testID={rightLinkTestID}
        >
          <Text style={styles.titleText}>{rightLinkText}</Text>
          <Icon name="Arrow Right" size={20} color={Colors.role.textStrong} />
        </Pressable>
      )}
      {rightActions && <View style={styles.headerActions}>{rightActions}</View>}
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  header: {
    height: 56,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    marginBottom: 2,
    backgroundColor: role.canvas,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.lg,
    gap: tokens.spacing.sm,
  },
  headerLeft: {
    flexShrink: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'hidden',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    flexShrink: 1,
    minWidth: 0,
  },
  titleText: {
    ...tokens.typography.body.mediumEmphasised,
    color: role.textStrong,
    flexShrink: 1,
  },
  countText: {
    ...tokens.typography.body.small,
    color: role.textSoft,
  },
  titleAccessory: {
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  rightLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    flexShrink: 0,
  },
  headerActions: {
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.md,
  },
}));
